import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download } from 'lucide-react';
import { AnnotatedDocument } from '@/types/dora-annotations';
import { EditableSection } from './EditableSection';
import { toast } from 'sonner';

interface DORAAnnotatedViewProps {
  annotatedDoc: AnnotatedDocument;
  onGenerateFinal: (editedSections: Record<string, string>) => void;
  onBack: () => void;
}

export function DORAAnnotatedView({
  annotatedDoc,
  onGenerateFinal,
  onBack
}: DORAAnnotatedViewProps) {
  const [editedSections, setEditedSections] = useState<Map<string, string>>(new Map());

  const handleEditSection = (sectionId: string, newText: string) => {
    setEditedSections(prev => new Map(prev).set(sectionId, newText));
  };

  const handleApplySuggestion = (sectionId: string, suggestionText: string) => {
    setEditedSections(prev => new Map(prev).set(sectionId, suggestionText));
    toast.success('Suggestion applied');
  };

  const handleGenerateFinal = () => {
    const sectionsObject: Record<string, string> = {};
    editedSections.forEach((text, id) => {
      sectionsObject[id] = text;
    });
    onGenerateFinal(sectionsObject);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header with actions */}
      <div className="border-b p-4 bg-white dark:bg-gray-950">
        <div className="flex items-center justify-between mb-4">
          <Button variant="outline" onClick={onBack}>
            ← Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => toast.info('Export feature coming soon')}>
              <Download className="h-4 w-4 mr-2" />
              Export Analysis Report
            </Button>
            <Button onClick={handleGenerateFinal}>
              Generate Final Document
            </Button>
          </div>
        </div>

        {/* Summary bar */}
        <div className="flex gap-4 text-sm flex-wrap">
          <Badge variant="outline" className="bg-green-50 dark:bg-green-950">
            ✅ {annotatedDoc.summary.total_blanks_filled} blanks filled
          </Badge>
          <Badge variant="outline" className="bg-red-50 dark:bg-red-950">
            🔴 {annotatedDoc.summary.critical_issues} critical
          </Badge>
          <Badge variant="outline" className="bg-yellow-50 dark:bg-yellow-950">
            🟡 {annotatedDoc.summary.medium_issues} medium
          </Badge>
          <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950">
            ℹ️ {annotatedDoc.summary.low_issues} low
          </Badge>
          {annotatedDoc.seller_standard && (
            <Badge variant="outline" className="bg-gray-50 dark:bg-gray-900">
              📄 Compared to: {annotatedDoc.seller_standard}
            </Badge>
          )}
        </div>
      </div>

      {/* Side-by-side document view */}
      <div className="flex-1 grid grid-cols-2 gap-0 overflow-hidden">
        {/* Left: Buyer's original template (read-only reference) */}
        <div className="border-r overflow-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-6">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-4 sticky top-0 bg-gray-50 dark:bg-gray-900 py-2">
              BUYER TEMPLATE (Reference)
            </div>
            {annotatedDoc.sections.map(section => (
              <div key={section.section_id} className="mb-8">
                <h3 className="font-semibold text-lg mb-3">{section.section_title}</h3>
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap font-serif text-gray-700 dark:text-gray-300">
                    {section.original_text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Your response with inline annotations (editable) */}
        <div className="overflow-auto bg-white dark:bg-gray-950">
          <div className="p-6">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-4 sticky top-0 bg-white dark:bg-gray-950 py-2">
              YOUR RESPONSE (Editable)
            </div>
            {annotatedDoc.sections.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p>No sections found. Document may need better structure.</p>
              </div>
            ) : (
              annotatedDoc.sections.map(section => (
                <EditableSection
                  key={section.section_id}
                  section={section}
                  editedText={editedSections.get(section.section_id)}
                  onEdit={(text) => handleEditSection(section.section_id, text)}
                  onApplySuggestion={(text) => handleApplySuggestion(section.section_id, text)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
